import { State } from '../../../reducers';
import { createSelector } from '@ngrx/store';
import { FacilityGroup } from '../../model/facility-group.model';
import { Facility } from '../../model/facility.model';
import { AlertStatus } from '../../model/alert.model';
import { FacilityPerformanceYearly } from 'brain-data/model/facility-performance.model';
import { selectChargepointSummary } from './chargepoint.selector';

export const selectFacilityData = (state: State) => state.facility;

export const selectFacilityGroups = createSelector(selectFacilityData, facilityState => facilityState.facilityGroups);
export const selectSortedFacilityGroups = createSelector(selectFacilityGroups, facilityGroups => {
  const sortedGroups = [...facilityGroups];
  sortedGroups.sort((a, b) => a.name.localeCompare(b.name));
  return sortedGroups;
});

export const selectAllFacilityGroupsFlattened = createSelector(selectFacilityGroups, facilityGroups => {
  const result: { group: FacilityGroup; depth: number }[] = [];

  const traverse = (groupList: FacilityGroup[], depth: number) => {
    const sorted = [...groupList].sort((a, b) => a.name.localeCompare(b.name));
    sorted.forEach(group => {
      result.push({ group, depth });
      if (group.subGroups && group.subGroups.length > 0) {
        traverse(group.subGroups, depth + 1);
      }
    });
  };

  traverse(facilityGroups, 0);
  return result;
});
export const selectFacilityisSavingLoading = createSelector(selectFacilityData, facilityState => facilityState.isSavingLoading);

export const selectFacilities = createSelector(selectFacilityData, facilityState => facilityState.facilities);

export const selectFacilitiesLoaded = createSelector(selectFacilityData, facilityState => facilityState.facilitiesLoaded);

export const selectFacilitiesCount = createSelector(selectFacilities, facilities => facilities.length);
export const selectFacilitiesWithAlerts = createSelector(selectFacilities, facilities => facilities.filter(facility => facility.alertCount > 0));
export const selectFacilitiesWithOpenAlerts = createSelector(selectFacilitiesWithAlerts, facilities =>
  facilities
    .map(facility => {
      const alerts = facility.alerts.filter(alert => alert.status === AlertStatus.open);
      return { ...facility, alerts, alertCount: alerts.length };
    })
    .filter(facility => facility.alertCount > 0),
);
export const selectFacilitiesWithClosedAlerts = createSelector(selectFacilitiesWithAlerts, facilities =>
  facilities
    .map(facility => {
      const alerts = facility.alerts.filter(alert => alert.status === AlertStatus.closed);
      return { ...facility, alerts, alertCount: alerts.length };
    })
    .filter(facility => facility.alertCount > 0),
);
export const selectFacility = createSelector(selectFacilityData, facilityState => facilityState.facility);

export const selectFacilityGroup = createSelector(selectFacilityData, facilityState => facilityState.facilityGroup);

export const selectFacilityPerformanceLoading = createSelector(selectFacilityData, facilityState => facilityState.facilityPerformanceLoading);
export const selectFacilityPerformance = createSelector(selectFacilityData, facilityState => facilityState.facilityPerformance);
export const selectFacilityPerformanceWithAggregatedYearEuis = createSelector(
  selectFacilityPerformance,
  (performances: FacilityPerformanceYearly[]): { [year: string]: number } => {
    const euiAccumulator: { [year: string]: { totalEui: number; count: number } } = {};

    performances.forEach(({ yearEUIs }) => {
      yearEUIs &&
        Object.entries(yearEUIs).forEach(([year, eui]) => {
          if (!euiAccumulator[year]) {
            euiAccumulator[year] = { totalEui: 0, count: 0 };
          }
          if (eui > 0) {
            euiAccumulator[year].totalEui += eui;
            euiAccumulator[year].count += 1;
          }
        });
    });

    return Object.keys(euiAccumulator).reduce((acc: { [year: string]: number }, year) => {
      const { totalEui, count } = euiAccumulator[year];
      acc[year] = count > 0 ? totalEui / count : 0;
      return acc;
    }, {});
  },
);
export const selectAverageEUIExcludingZeros = createSelector(
  selectFacilityPerformanceWithAggregatedYearEuis,
  (aggregatedEuis: { [year: number]: number }): number => {
    let totalEUI = 0;
    let count = 0;

    Object.values(aggregatedEuis).forEach(eui => {
      if (eui > 0) {
        totalEUI += eui;
        count++;
      }
    });
    return count > 0 ? totalEUI / count : 0;
  },
);
export const selectFacilitySolarPerformance = createSelector(selectFacilityData, facilityState => facilityState.facilitySolarPerformance);

export const selectFacilitiesForGroup = createSelector(selectFacilities, selectFacilityGroup, (facilities, group) => filterFacilities(facilities, group));

export const selectFacilitiesForGroupSorted = createSelector(selectFacilitiesForGroup, facilities => {
  if (!facilities || facilities.length === 0) {
    return facilities;
  }
  return [...facilities].sort((a, b) => a.name.localeCompare(b.name));
});

export const selectFacilitiesForGroupWithDescendants = createSelector(
  selectFacilities,
  selectFacilityGroup,
  selectFacilityGroups,
  (facilities, currentGroup, allGroups) => {
    if (!currentGroup) {
      return facilities;
    }
    return filterFacilitiesDeep(facilities, currentGroup, allGroups);
  },
);
export const selectFacilitiesForGroupOrFacility = createSelector(selectFacilitiesForGroup, selectFacility, (facilities, facility) => {
  if (facility) {
    return [facility];
  } else {
    return facilities;
  }
});

export const filterFacilities = (facilities: Facility[], group?: FacilityGroup) =>
  group ? facilities.filter(facility => facility.groups.includes(group.name)) : facilities;

const findFacilityGroupById = (id: number, groups: FacilityGroup[]): FacilityGroup | undefined => {
  for (const group of groups) {
    if (group.id === id) {
      return group;
    }
    if (group.subGroups?.length) {
      const subgroup = findFacilityGroupById(id, group.subGroups);
      if (subgroup) {
        return subgroup;
      }
    }
  }
  return undefined;
};

export const filterFacilitiesDeep = (facilities: Facility[], currentGroup: FacilityGroup, allGroups: FacilityGroup[]): Facility[] => {
  if (!currentGroup) return facilities;

  const fullCurrentGroup = findFacilityGroupById(currentGroup.id, allGroups) || currentGroup;

  const groupNames = new Set<string>();
  const collectGroupNames = (group: FacilityGroup) => {
    if (group.name) {
      groupNames.add(group.name);
    }
    group.subGroups?.forEach(collectGroupNames);
  };
  collectGroupNames(fullCurrentGroup);

  const seenIds = new Set<number>();
  return facilities.filter(facility => {
    if (facility.id && seenIds.has(facility.id)) {
      return false;
    }
    const matches = facility.groups.some(groupName => groupNames.has(groupName));
    if (matches && facility.id) {
      seenIds.add(facility.id);
    }
    return matches;
  });
};

export const selectFacilitiesWithChargepoints = createSelector(selectFacilities, selectChargepointSummary, (facilities, chargepointSummary) => {
  if (!chargepointSummary || chargepointSummary.length === 0) {
    return [];
  }

  const chargepointFacilityNames = [...new Set(chargepointSummary.map(item => item.facilityName))];

  return facilities.filter(facility => chargepointFacilityNames.includes(facility.name));
});

export const selectUnmappedFacilities = createSelector(selectFacilities, selectFacilityGroups, (facilities, groups) => {
  const unmatchedGroupNames = new Set<string>();

  const collectUnmatchedGroups = (groupList: FacilityGroup[]) => {
    for (const group of groupList || []) {
      if (group.unmatchedFacility === true) {
        unmatchedGroupNames.add(group.name);
      }
      if (group.subGroups && group.subGroups.length > 0) {
        collectUnmatchedGroups(group.subGroups);
      }
    }
  };

  collectUnmatchedGroups(groups || []);

  return (facilities || []).filter(f => {
    const assigned = f.groups || [];
    return assigned.some(name => unmatchedGroupNames.has(name));
  });
});
